import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Users, Target, Award, Activity, FileSpreadsheet, FileText, CalendarRange } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CHART_COLORS = ['#0082D2', '#10B981', '#F59E0B', '#E32219', '#00205B', '#AF52DE'];
const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const TOOLTIP_STYLE = { background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12, color: '#0A0A0A' };
const AXIS_TICK_DASH = { fontSize: 11, fill: '#6B7280' };
const H_BAR_RADIUS = [0, 4, 4, 0];

export default function Dashboard() {
  const { canManage } = useAuth();
  const [stats, setStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [supervisorData, setSupervisorData] = useState([]);
  const [vendorPerformance, setVendorPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, monthlyRes] = await Promise.all([
        api.getSalesStats(),
        api.getSalesByMonth(2026)
      ]);
      
      setStats(statsRes);
      setMonthlyData(monthlyRes.map(m => ({ ...m, name: MONTHS[m.mes - 1] })));
      
      if (statsRes.alerts) {
        statsRes.alerts.forEach(alert => {
          if (alert.type === 'info') {
            toast.info(alert.message);
          }
        });
      }

      if (canManage) {
        const [supervisorRes, perfRes] = await Promise.all([
          api.getSalesBySupervisor(),
          api.getVendorPerformance()
        ]);
        setSupervisorData(supervisorRes);
        setVendorPerformance(perfRes);
      }
    } catch (error) {
      /* handled */
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 600000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-[13px]">Cargando...</div>
      </div>
    );
  }

  const servicesData = stats?.services_count 
    ? Object.entries(stats.services_count).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
    : [];

  const exportWeeklyExcel = async () => {
    try {
      const weekData = await api.getWeeklyExport();
      if (weekData.total_registros === 0) {
        toast.info('No hay ventas esta semana');
        return;
      }
      const wb = XLSX.utils.book_new();
      
      // Create one sheet per vendor
      const vendors = weekData.vendors || {};
      Object.entries(vendors).forEach(([vendorName, sales]) => {
        const rows = sales.map(r => ({
          'Cedula': r.cedula,
          'Nombre': r.nombre,
          'Apellido': r.apellido,
          'Producto': r.producto,
          'Fecha': r.fecha
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 25 }, { wch: 12 }];
        // Sheet name max 31 chars
        const sheetName = vendorName.substring(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
      
      XLSX.writeFile(wb, `ventas_semana_${weekData.semana_inicio}_${weekData.semana_fin}.xlsx`);
      toast.success(`Excel semanal exportado (${weekData.total_registros} registros, ${Object.keys(vendors).length} vendedores)`);
    } catch (error) {
      toast.error('Error al exportar ventas semanales');
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const resumen = [
      { 'Metrica': 'Total Ventas Este Mes', 'Valor': stats?.total_services || 0 },
      { 'Metrica': 'Socios Atendidos', 'Valor': stats?.total_socios || 0 },
      { 'Metrica': 'Fonasa Hoy', 'Valor': stats?.fonasa_today || 0 },
      { 'Metrica': 'Mejor Vendedor', 'Valor': `${stats?.best_vendor || 'N/A'} (${stats?.best_vendor_count || 0})` },
    ];
    const wsResumen = XLSX.utils.json_to_sheet(resumen);
    wsResumen['!cols'] = [{ wch: 22 }, { wch: 25 }];
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

    const servicios = servicesData.map(s => ({ 'Servicio': s.name, 'Cantidad': s.value }));
    const wsServicios = XLSX.utils.json_to_sheet(servicios);
    wsServicios['!cols'] = [{ wch: 28 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsServicios, 'Por Servicio');

    if (stats?.goals_progress) {
      const metas = Object.entries(stats.goals_progress).map(([k, v]) => ({
        'Servicio': k, 'Actual': v.current, 'Meta': v.goal, '%': v.percentage
      }));
      const wsMetas = XLSX.utils.json_to_sheet(metas);
      wsMetas['!cols'] = [{ wch: 28 }, { wch: 8 }, { wch: 8 }, { wch: 8 }];
      XLSX.utils.book_append_sheet(wb, wsMetas, 'Metas');
    }

    if (vendorPerformance.length > 0) {
      const rend = vendorPerformance.map(v => ({
        'Vendedor': v.nombre, 'Este Mes': v.servicios_mes,
        'Mes Anterior': v.servicios_mes_anterior, 'Tendencia': v.tendencia === 'up' ? 'Subiendo' : v.tendencia === 'down' ? 'Bajando' : 'Igual'
      }));
      const wsRend = XLSX.utils.json_to_sheet(rend);
      wsRend['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 14 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsRend, 'Rendimiento');
    }

    XLSX.writeFile(wb, `dashboard_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Dashboard - Departamento Comercial', 14, 22);
    doc.setFontSize(10);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-UY')}`, 14, 30);
    doc.setFontSize(12);
    doc.text(`Total Ventas: ${stats?.total_services || 0} | Socios: ${stats?.total_socios || 0} | Fonasa Hoy: ${stats?.fonasa_today || 0}`, 14, 40);

    const servData = servicesData.map(s => [s.name, s.value]);
    autoTable(doc, {
      head: [['Servicio', 'Cantidad']],
      body: servData,
      startY: 47,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 32, 91] }
    });

    if (stats?.goals_progress) {
      const metasData = Object.entries(stats.goals_progress).map(([k, v]) => [k, v.current, v.goal, `${v.percentage}%`]);
      autoTable(doc, {
        head: [['Servicio', 'Actual', 'Meta', '%']],
        body: metasData,
        startY: doc.lastAutoTable.finalY + 10,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [16, 185, 129] }
      });
    }

    if (vendorPerformance.length > 0) {
      const rendData = vendorPerformance.map(v => [
        v.nombre, v.servicios_mes, v.servicios_mes_anterior,
        v.tendencia === 'up' ? 'Subiendo' : v.tendencia === 'down' ? 'Bajando' : 'Igual'
      ]);
      autoTable(doc, {
        head: [['Vendedor', 'Este Mes', 'Mes Anterior', 'Tendencia']],
        body: rendData,
        startY: doc.lastAutoTable.finalY + 10,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [0, 130, 210] }
      });
    }

    doc.save(`dashboard_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exportado');
  };

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Resumen de actividad comercial</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <button onClick={exportWeeklyExcel} className="btn-primary flex items-center gap-2" data-testid="dashboard-export-weekly">
              <CalendarRange size={16} />
              Reporte Semanal
            </button>
          )}
          <button onClick={exportToExcel} className="btn-secondary flex items-center gap-2" data-testid="dashboard-export-excel">
            <FileSpreadsheet size={16} />
            Excel
          </button>
          <button onClick={exportToPDF} className="btn-secondary flex items-center gap-2" data-testid="dashboard-export-pdf">
            <FileText size={16} />
            PDF
          </button>
        </div>
      </div>

      {/* FONASA 5000 Goal Bar */}
      {stats && (
        <div className="mac-card p-6" data-testid="fonasa-goal-bar">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#00205B]/10 flex items-center justify-center">
                <Target size={20} className="text-[#00205B]" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Meta Fonasa 2026</h3>
                <p className="text-[11px] text-gray-500">Objetivo anual: 5.000 socios Fonasa</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-[#00205B]" style={{ fontFamily: 'Outfit, sans-serif', fontVariantNumeric: 'tabular-nums' }}>{(stats.fonasa_annual || 0).toLocaleString('es-UY')}</div>
              <div className="text-[11px] text-gray-500">de {(stats.fonasa_goal || 5000).toLocaleString('es-UY')}</div>
            </div>
          </div>
          <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000"
              style={{
                width: `${Math.min(100, ((stats.fonasa_annual || 0) / (stats.fonasa_goal || 5000)) * 100)}%`,
                background: 'linear-gradient(90deg, #0082D2, #00205B, #E32219)',
              }}
            />
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-gray-400">
            <span>0</span>
            <span>1.250</span>
            <span>2.500</span>
            <span>3.750</span>
            <span>5.000</span>
          </div>
        </div>
      )}

      {/* Alerts */}
      {stats?.alerts?.filter(a => a.type === 'success').length > 0 && (
        <div className="space-y-2">
          {stats.alerts.filter(a => a.type === 'success').map((alert, i) => (
            <div key={`alert-${alert.message.substring(0, 20)}-${i}`} className="alert-success" data-testid={`alert-${i}`}>
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="metric-card" data-testid="metric-total-services">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-md bg-[#0082D2]/10 flex items-center justify-center">
              <Activity size={16} className="text-[#0082D2]" />
            </div>
          </div>
          <div className="metric-value">{stats?.total_services || 0}</div>
          <div className="metric-label">Total Ventas Este Mes</div>
        </div>
        
        <div className="metric-card" data-testid="metric-total-socios">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-md bg-[#10B981]/10 flex items-center justify-center">
              <Users size={16} className="text-[#10B981]" />
            </div>
          </div>
          <div className="metric-value">{stats?.total_socios || 0}</div>
          <div className="metric-label">Socios Atendidos</div>
        </div>
        
        <div className="metric-card" data-testid="metric-fonasa-today">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-md bg-[#F59E0B]/10 flex items-center justify-center">
              <Target size={16} className="text-[#F59E0B]" />
            </div>
          </div>
          <div className="metric-value">{stats?.fonasa_today || 0}</div>
          <div className="metric-label">Fonasa Hoy</div>
        </div>
        
        <div className="metric-card" data-testid="metric-best-vendor">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-md bg-[#AF52DE]/10 flex items-center justify-center">
              <Award size={16} className="text-[#AF52DE]" />
            </div>
          </div>
          <div className="text-lg font-bold text-[#0A0A0A] truncate">{stats?.best_vendor || 'N/A'}</div>
          <div className="metric-label">{stats?.best_vendor_count || 0} ventas</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="mac-card p-5">
          <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Total Ventas por Mes 2026</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorServices" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0082D2" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#0082D2" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis dataKey="name" tick={AXIS_TICK_DASH} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK_DASH} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="servicios" stroke="#0082D2" strokeWidth={2} fillOpacity={1} fill="url(#colorServices)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {canManage && supervisorData.length > 0 && (
          <div className="mac-card p-5">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Por Supervisor</h3>
            <div className="h-56 flex items-center">
              <div className="w-1/2">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={supervisorData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="count" nameKey="supervisor">
                      {supervisorData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-2">
                {supervisorData.slice(0, 5).map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-[11px]">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[index] }} />
                    <span className="text-gray-500 truncate flex-1">{item.supervisor}</span>
                    <span className="text-[#0A0A0A] font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Services Bar Chart */}
      <div className="mac-card p-5">
        <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Por Producto</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={servicesData} layout="vertical" barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
              <XAxis type="number" tick={AXIS_TICK_DASH} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={AXIS_TICK_DASH} width={110} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" fill="#10B981" radius={H_BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Goals Progress */}
      <div className="mac-card p-5">
        <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Metas Mensuales</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats?.goals_progress && Object.entries(stats.goals_progress).map(([service, data]) => (
            <div key={service} className="bg-[#F3F4F6] rounded-md p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[12px] font-medium text-gray-700 truncate">{service}</span>
                <span className="text-[11px] text-gray-500">{data.current}/{data.goal}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ 
                  width: `${data.percentage}%`,
                  backgroundColor: data.percentage >= 100 ? '#10B981' : data.percentage >= 50 ? '#0082D2' : '#F59E0B'
                }} />
              </div>
              <div className="text-[10px] text-gray-500 mt-1">{data.percentage}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Vendor Performance */}
      {canManage && vendorPerformance.length > 0 && (
        <div className="mac-card overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Rendimiento por Vendedor</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Vendedor</th>
                  <th className="table-header">Este Mes</th>
                  <th className="table-header">Mes Anterior</th>
                  <th className="table-header">Tendencia</th>
                </tr>
              </thead>
              <tbody>
                {vendorPerformance.map((vendor) => (
                  <tr key={vendor.vendedor_id} className="table-row">
                    <td className="table-cell font-medium text-[#0A0A0A]">{vendor.nombre}</td>
                    <td className="table-cell">{vendor.servicios_mes}</td>
                    <td className="table-cell">{vendor.servicios_mes_anterior}</td>
                    <td className="table-cell">
                      {vendor.tendencia === 'up' && <span className="inline-flex items-center gap-1 text-[#10B981] text-[12px]"><TrendingUp size={14} /> Subiendo</span>}
                      {vendor.tendencia === 'down' && <span className="inline-flex items-center gap-1 text-[#EF4444] text-[12px]"><TrendingDown size={14} /> Bajando</span>}
                      {vendor.tendencia === 'equal' && <span className="inline-flex items-center gap-1 text-gray-500 text-[12px]"><Minus size={14} /> Igual</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
